import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { BellOff, Siren } from "lucide-react";
import { api } from "../lib/api";
import { ensurePushSubscribed, unsubscribePush, pushStatus } from "../lib/push";

// Non-sensitive UI-only flags. Server is the source of truth for on_call.
// These keys hold no PII/tokens/secrets — only ephemeral UX preferences.
const UI_TS_KEY = "raf_plantao_on_at";           // last-toggle timestamp (auto-reset window)
const UI_SNOOZE_KEY = "raf_plantao_snoozed_until"; // client-side snooze pref
const UI_KICK_KEY = "raf_plantao_kicked_seen_at";  // last observed off_at (dedup toast)
const AUTO_RESET_MS = 12 * 60 * 60 * 1000; // 12h

/**
 * "Modo Plantão" toggle — server-authoritative.
 *
 * Server (POST /consultant/plantao/toggle → users.on_call) is the source of
 * truth. localStorage now holds only non-sensitive UX flags (timestamps,
 * snooze prefs). No PII, tokens, or auth data are cached client-side.
 */
export const PlantaoToggle = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(false);
  const [scheduleActive, setScheduleActive] = useState(false);

  const dispatchChange = useCallback((next, meta = {}) => {
    window.dispatchEvent(new CustomEvent("raf-plantao-changed", { detail: { on: next, ...meta } }));
  }, []);

  // Hydrate initial state from server — no localStorage-based auth cache.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        const nowOn = !!data?.on_call;
        setOn(nowOn);
        dispatchChange(nowOn, { source: "hydrate" });
        // Auto-reset guard: if server says ON for >12h without recent toggle, warn
        const ts = parseInt(localStorage.getItem(UI_TS_KEY) || "0", 10);
        if (nowOn && ts && Date.now() - ts > AUTO_RESET_MS) {
          toast.info("Modo Plantão ativo há mais de 12h — considere desligar se não estiver disponível.");
        }
      } catch (_) { /* not logged in as consultor */ }
      pushStatus().then((s) => { if (!cancelled) setSupported(!!s.supported); });
    })();
    return () => { cancelled = true; };
  }, [dispatchChange]);

  // "Escala roubada" — detecta kick REMOTO do admin (transparência).
  // Ignora self-toggles (usuário desligou próprio plantão) e ignora quando o
  // usuário já reativou (on_call=true em cima do off_at antigo).
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        const off_at = data?.on_call_off_at || "";
        if (!off_at) return;
        // Se o consultor já está de volta em plantão, o off_at é histórico — não avisa.
        if (data?.on_call) return;
        const who = data?.on_call_off_by_name || "";
        // Self-toggle não é "kick" — usuário sabe que desligou. Silencia toast.
        if (who === "self" || (who && data?.name && who === data.name)) {
          // ainda armazena o timestamp pra dedup se admin remover depois
          localStorage.setItem(UI_KICK_KEY, off_at);
          return;
        }
        const lastSeen = localStorage.getItem(UI_KICK_KEY) || "";
        if (off_at !== lastSeen) {
          localStorage.setItem(UI_KICK_KEY, off_at);
          if (lastSeen) {
            const whoLabel = who || "Admin RAF";
            const when = new Date(off_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
            toast.warning(
              `Você foi retirado do plantão por ${whoLabel} às ${when}. Reative o Modo Plantão manualmente se necessário.`,
              { duration: 12000 },
            );
            setOn(false);
            dispatchChange(false, { kicked: true });
          }
        }
      } catch (_) { /* ignore */ }
    };
    check();
    const id = setInterval(check, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [dispatchChange]);

  // Auto-ativar plantão dentro do slot da escala semanal.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const [schedRes, meRes] = await Promise.all([
          api.get("/consultant/me/schedule"),
          api.get("/auth/me"),
        ]);
        if (cancelled) return;
        const now = new Date();
        // JS: 0=Domingo..6=Sábado. Backend: 0=Segunda..6=Domingo.
        const weekday = (now.getDay() + 6) % 7;
        const hour = now.getHours() + now.getMinutes() / 60;
        const inSlot = (schedRes.data || []).some((s) =>
          s.weekday === weekday && hour >= s.start_hour && hour < s.end_hour,
        );
        setScheduleActive(inSlot);
        const snoozedUntil = parseInt(localStorage.getItem(UI_SNOOZE_KEY) || "0", 10);
        const snoozed = snoozedUntil > Date.now();
        const currentlyOn = !!meRes.data?.on_call;
        if (inSlot && !currentlyOn && !snoozed) {
          try { await api.post("/consultant/plantao/toggle", { on: true }); } catch (_) { return; }
          localStorage.setItem(UI_TS_KEY, String(Date.now()));
          setOn(true);
          dispatchChange(true, { auto: true });
          toast.success("Modo Plantão ativado pela escala.", { duration: 6000 });
        }
      } catch (_) { /* sem escala ou não-consultor */ }
    };
    check();
    const id = setInterval(check, 5 * 60 * 1000); // 5min
    return () => { cancelled = true; clearInterval(id); };
  }, [dispatchChange]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!on) {
        try { await api.post("/consultant/plantao/toggle", { on: true }); }
        catch (err) {
          toast.error(err?.response?.data?.detail || "Erro ao ativar plantão no servidor");
          setBusy(false); return;
        }
        localStorage.setItem(UI_TS_KEY, String(Date.now()));
        localStorage.removeItem(UI_SNOOZE_KEY);
        if (supported) {
          const res = await ensurePushSubscribed();
          if (!res.ok && res.reason !== "unsupported") {
            toast.warning("Plantão ativado. Push do navegador não pôde ser habilitado, mas o destaque de casos emergenciais continua funcionando.");
          } else {
            toast.success("Plantão ativado. Você receberá alertas de emergência.");
          }
        } else {
          toast.success("Plantão ativado. Casos emergenciais serão destacados.");
        }
        setOn(true);
        dispatchChange(true);
      } else {
        try { await api.post("/consultant/plantao/toggle", { on: false }); }
        catch (_) { /* best-effort — libera UI mesmo se falhar */ }
        localStorage.removeItem(UI_TS_KEY);
        // Se desligar manualmente durante a escala, snooze até virar o dia
        if (scheduleActive) {
          const t = new Date();
          t.setHours(23, 59, 59, 999);
          localStorage.setItem(UI_SNOOZE_KEY, String(t.getTime()));
        }
        if (supported) {
          try { await unsubscribePush(); } catch (_) { /* best-effort */ }
        }
        toast.info("Plantão desativado.");
        setOn(false);
        dispatchChange(false);
      }
    } catch (_) {
      toast.error("Erro ao alternar plantão.");
    } finally { setBusy(false); }
  };

  return (
    <button
      data-testid="plantao-toggle"
      data-active={on ? "true" : "false"}
      onClick={toggle}
      disabled={busy}
      title={on ? "Modo Plantão ativo — alertas de emergência ligados" : "Ative o modo Plantão para receber alertas"}
      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full border text-[11px] font-semibold transition-colors ${
        on
          ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
          : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
      } disabled:opacity-50`}
    >
      {on ? (
        <span className="relative inline-flex w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-70" />
          <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-red-600" />
        </span>
      ) : <BellOff className="w-3 h-3" strokeWidth={1.8} />}
      <span className="hidden sm:inline">{on ? "Plantão" : "Plantão"}</span>
    </button>
  );
};

/** Hook para páginas escutarem o estado do plantão. Fonte: server → evento custom. */
export const usePlantao = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    // Hydrate from server on mount (no localStorage cache of auth state).
    api.get("/auth/me")
      .then(({ data }) => { if (!cancelled) setOn(!!data?.on_call); })
      .catch(() => {});
    const h = (e) => setOn(!!e.detail?.on);
    window.addEventListener("raf-plantao-changed", h);
    return () => { cancelled = true; window.removeEventListener("raf-plantao-changed", h); };
  }, []);
  return on;
};
